import { z } from "zod";

import {
	type ClosedTodayResponse,
	type ClosingAnalyticsResponse,
	closingAnalyticsResponseSchema,
	type ClosingCartonsResponse,
	closingCartonsResponseSchema,
} from "@/schemas";
import { baseRoutes } from "@/api/base-routes";
import { packNetAxios } from "@/lib/api-client";
import { type SelectedCarton, selectedCartonSchema } from "@/schemas/routing/selected-carton";

export interface GetClosingCartonsParams {
	identifier: string | null;
	pageNumber: number;
	pageSize: number;
	closingType: "Available" | "Active" | "Failed";
}

export const getClosingCartons = async ({
	identifier,
	pageNumber,
	pageSize,
	closingType,
}: GetClosingCartonsParams): Promise<ClosingCartonsResponse> => {
	const endpoint = `${baseRoutes.productionPipeline}/v1/Closing/closings`;

	const response = await packNetAxios.post(endpoint, {
		identifier,
		pageNumber,
		pageSize,
		type: closingType,
	});

	if (response.status !== 200) {
		throw new Error(`Failed to fetch production jobs: ${response.status}`);
	}

	const validatedResponse = closingCartonsResponseSchema
		.enrich({ callSite: "getClosingCartons" })
		.safeParseWithTelemetry(response.data);

	if (!validatedResponse.success) {
		console.error("Failed to validate closing cartons response:", validatedResponse.error);
		return response.data;
	}

	return validatedResponse.data;
};

export const getClosedToday = async (machineId?: string, timezone?: string): Promise<ClosedTodayResponse> => {
	const params = new URLSearchParams();
	if (machineId) {
		params.append("machineId", machineId);
	}
	if (timezone) {
		params.append("timezone", timezone);
	}

	const queryString = params.toString();
	const url = `${baseRoutes.productionPipeline}/v1/Closing/totalClosedToday${queryString ? `?${queryString}` : ""}`;

	const response = await packNetAxios.get<ClosedTodayResponse>(url);

	return response.data;
};

export const closeManually = async (packagingSolutionId: string, closingId: string) => {
	return await packNetAxios.put(
		`${baseRoutes.productionPipeline}/v1/Closing/packagingSolution/closeManually/byId/${packagingSolutionId}?closingId=${closingId}`,
	);
};

export const removeManually = async (packagingSolutionId: string, closingId: string) => {
	return await packNetAxios.put(
		`${baseRoutes.productionPipeline}/v1/Closing/packagingSolution/removeManually/byId/${packagingSolutionId}?closingId=${closingId}`,
	);
};

export const reclosePackagingSolution = async (packagingSolutionId: string) => {
	return await packNetAxios.post(
		`${baseRoutes.productionPipeline}/v1/Closing/packagingSolution/reclose?packagingSolutionId=${packagingSolutionId}`,
	);
};

export const getClosingAnalytics = async (): Promise<ClosingAnalyticsResponse> => {
	const response = await packNetAxios.get<ClosingAnalyticsResponse>(
		`${baseRoutes.productionPipeline}/v1/Closing/closingAnalytics`,
	);

	const validatedResponse = closingAnalyticsResponseSchema
		.enrich({ callSite: "getClosingAnalytics" })
		.safeParseWithTelemetry(response.data);

	if (!validatedResponse.success) {
		console.error("Failed to validate closing analytics response:", validatedResponse.error);
		throw new Error("Invalid response from server");
	}

	return validatedResponse.data;
};

export const getSelectedCartons = async (): Promise<SelectedCarton[]> => {
	const response = await packNetAxios.get<SelectedCarton[]>(
		`${baseRoutes.productionPipeline}/v1/productionStatus/selectedCartons`,
	);

	return z.array(selectedCartonSchema).enrich({ callSite: "getSelectedCartons" }).parseWithTelemetry(response.data);
};

export async function getVersion(): Promise<string> {
	const response = await packNetAxios.get<string>(`${baseRoutes.productionPipeline}/v1/Closing/version`);
	return response.data;
}
